import type { UIMessage } from "ai";
import { createContext, type ReactNode, useContext } from "react";
import { useChatModelState } from "./hooks/use-chat-model";
import { useChatSession } from "./hooks/use-chat-session";
import { useChatToolkitsState } from "./hooks/use-chat-toolkits";
import type { OnToolFinish } from "./types";

type ChatRuntimeState = ReturnType<typeof useChatSession>;
type ChatToolkitsState = ReturnType<typeof useChatToolkitsState>;
type ChatModelState = ReturnType<typeof useChatModelState>;

type ChatProviderProps = {
  baseUrl: string;
  viewerId: string;
  organizationId: string;
  projectId: string;
  initialChatId?: string;
  initialMessages?: UIMessage[];
  onAuthError?: () => void;
  onChatPersisted?: (chatId: string) => void;
  onToolFinish?: OnToolFinish;
  children: ReactNode;
};

const ChatRuntimeContext = createContext<ChatRuntimeState | null>(null);
const ChatToolkitsContext = createContext<ChatToolkitsState | null>(null);
const ChatModelContext = createContext<ChatModelState | null>(null);

function useRequiredContext<T>(value: T | null, name: string): T {
  if (value === null) {
    throw new Error(`${name} must be used within a ChatProvider`);
  }
  return value;
}

export function useChatRuntime() {
  return useRequiredContext(useContext(ChatRuntimeContext), "useChatRuntime");
}

export function useChatToolkits() {
  return useRequiredContext(useContext(ChatToolkitsContext), "useChatToolkits");
}

export function useChatModel() {
  return useRequiredContext(useContext(ChatModelContext), "useChatModel");
}

export function ChatProvider({
  children,
  baseUrl,
  viewerId,
  organizationId,
  projectId,
  initialChatId,
  initialMessages,
  onAuthError,
  onChatPersisted,
  onToolFinish,
}: ChatProviderProps) {
  const toolkits = useChatToolkitsState({
    baseUrl,
    organizationId,
    projectId,
    viewerId,
  });

  const model = useChatModelState({
    baseUrl,
    organizationId,
    projectId,
    viewerId,
  });

  const chat = useChatSession({
    baseUrl,
    viewerId,
    initialChatId,
    initialMessages,
    onAuthError,
    getBody: () => ({
      organizationId,
      projectId,
      ...(toolkits.selectedToolkits.length > 0 && {
        toolkits: toolkits.selectedToolkits,
      }),
      ...(toolkits.toolsForAI.length > 0 && { tools: toolkits.toolsForAI }),
      ...(model.selectedModel && { model: model.selectedModel }),
    }),
    onChatPersisted,
    onToolFinish,
  });

  return (
    <ChatRuntimeContext value={chat}>
      <ChatToolkitsContext value={toolkits}>
        <ChatModelContext value={model}>{children}</ChatModelContext>
      </ChatToolkitsContext>
    </ChatRuntimeContext>
  );
}
